import React, { useState } from "react";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { AiFillEyeInvisible } from "react-icons/ai";
import { Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllSubAdmins,
  addSubAdmin,
  updateSubAdmin,
  deleteSubAdmin,
} from "../../../../api/owner/subadmins";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import Pagination from "../../../../components/common/Pagination";
import { useTranslation } from "../../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const AdminSubAdminRoles = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const initialFormState = {
    name: "",
    email: "",
    password: "",
    phoneNumber: "",
    role: "",
    assignedFor: "restaurant",
    status: "active",
  };
  const initialErrorState = {
    name: "",
    email: "",
    password: "",
    phoneNumber: "",
    role: "",
  };
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState(initialErrorState);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 5;
  const [showPassword, setShowPassword] = useState(false);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["sub-admins", page],
    queryFn: () => getAllSubAdmins({ page, limit, assignedFor: "restaurant" }),
    keepPreviousData: true,
  });

  const subAdmins = data?.data?.subAdmins || [];
  const pagination = data?.data?.pagination || {};

  const { mutate: addSubAdminMutation , isPending : isAdding } = useMutation({
    mutationFn: addSubAdmin,
    onSuccess: () => {
      const successMessage = t("subAdmin").addsuccess;
      toast.success(successMessage);
      queryClient.invalidateQueries(["sub-admins"]);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("managesubAdmin").toastMessages.addError;
      toast.error(errorMessage);
    },
  });

  const { mutate: updateSubAdminMutation , isPending : isUpdating } = useMutation({
    mutationFn: ({ id, data }) => updateSubAdmin(id, data),
    onSuccess: () => {
      const successMessage = t("subAdmin").updatesuccess;
      toast.success(successMessage);
      queryClient.invalidateQueries(["sub-admins"]);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("managesubAdmin").toastMessages.updateError;
      toast.error(errorMessage);
    },
  });

  const { mutate: deleteSubAdminMutation } = useMutation({
    mutationFn: deleteSubAdmin,
    onSuccess: () => {
      const successMessage = t("subAdmin").deletesuccess;
      toast.success(successMessage);
      queryClient.invalidateQueries(["sub-admins"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("managesubAdmin").toastMessages.deleteError;
      toast.error(errorMessage);
    },
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleAddOrUpdate = () => {
    const isValid = validateForm(formData, setErrors);
    if (!isValid) return;

    if (editId) {
      updateSubAdminMutation({ id: editId, data: formData });
    } else {
      addSubAdminMutation(formData);
    }
  };

  const validateForm = (data) => {
    const errors = {};

    if (!data.name.trim()) {
      errors.name = t("managesubAdmin").validation.nameRequired;
    }

    if (!data.email.trim()) {
      errors.email = t("managesubAdmin").validation.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = t("managesubAdmin").validation.emailInvalid;
    }

    if (!data.phoneNumber.trim()) {
      errors.phoneNumber = t("managesubAdmin").validation.phoneRequired;
    } else if (!/^\d{10,15}$/.test(data.phoneNumber)) {
      errors.phoneNumber = t("managesubAdmin").validation.phoneInvalid;
    }

    if (!data.role) {
      errors.role = t("managesubAdmin").validation.roleRequired;
    }

    // Password is required only when adding new sub-admin (not when editing)
    if (!editId && !data.password.trim()) {
      errors.password = t("managesubAdmin").validation.passwordRequired;
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = (subadmin) => {
    const updatedData = {
      name: subadmin.name || "",
      email: subadmin.email || "",
      password: "",
      phoneNumber: subadmin.phoneNumber || "",
      role: subadmin.role || "",
      assignedFor: "restaurant",
      status: subadmin.status,
    };

    setFormData(updatedData);
    setEditId(subadmin._id);
    setIsPopupOpen(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t("managesubAdmin").deleteConfirm.title,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("managesubAdmin").deleteConfirm.confirm,
      cancelButtonText: t("managesubAdmin").deleteConfirm.cancel,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteSubAdminMutation(id);
      }
    });
  };

  const closeModal = () => {
    setIsPopupOpen(false);
    setEditId(null);
    setShowPassword(false);
    setFormData(initialFormState);
    setErrors(initialErrorState);
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-[22px] font-semibold text-black">
          {" "}
          {t("managesubAdmin").title}
        </h2>
        <button
          onClick={() => setIsPopupOpen(true)}
          className="inline-block max-w-[205px] rounded-[2px] bg-[#4CAF50] text-white font-semibold p-3 green-shadow hover:bg-[#209b25]"
        >
          {t("managesubAdmin").addButton}
        </button>
      </div>

      <div className="mt-[30px]">
        <div className="border rounded-[10px] bg-white mt-[28px] py-[20px] px-[15px] sm:px-[30px]">
          <h2 className="text-base font-semibold">
            {t("managesubAdmin").listTitle}
          </h2>

          {isPending ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <p className="text-red-500 text-center mt-6">{error.message}</p>
          ) : (
            <>
              <div className="overflow-x-auto mt-[30px]">
                <table className="min-w-full">
                  <thead className="bg-[#FFFAF2]">
                    <tr>
                      <th className="py-[12px] px-[16px] text-left">
                        {t("managesubAdmin").columns.name}
                      </th>
                      <th className="py-[12px] px-[16px] text-left">
                        {t("managesubAdmin").columns.email}
                      </th>
                      <th className="py-[12px] px-[16px] text-left">
                        {t("managesubAdmin").columns.phone}
                      </th>
                      <th className="py-[12px] px-[16px] text-left">
                        {t("managesubAdmin").columns.roles}
                      </th>
                      <th className="py-[12px] px-[16px] text-left">
                        {t("managesubAdmin").columns.status}
                      </th>
                      <th className="py-[12px] px-[16px] text-left">
                        {t("managesubAdmin").columns.actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subAdmins.map((subadmin) => (
                      <tr key={subadmin._id} className="border-t">
                        <td className="py-[12px] px-[16px]">{subadmin.name}</td>
                        <td className="py-[12px] px-[16px]">
                          {subadmin.email}
                        </td>
                        <td className="py-[12px] px-[16px]">
                          {subadmin.phoneNumber}
                        </td>
                         <td className="py-[12px] px-[16px]">
                          {t("subAdmin").rolesOptions[subadmin.role] || subadmin.role}
                        </td>
                        <td className="py-[12px] px-[16px]">
                          <span
                            className={
                              subadmin.status === "active"
                                ? "text-green-600 font-semibold"
                                : "text-red-600 font-semibold"
                            }
                          >
                            {t("managesubAdmin").status[subadmin.status] || subadmin.status}
                          </span>
                        </td>

                        <td className="py-[12px] px-[16px] flex gap-[12px]">
                          <button
                            onClick={() => handleEdit(subadmin)}
                            className="bg-[#F2F2F2] p-2 rounded-full"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(subadmin._id)}
                            className="bg-[#F2F2F2] p-2 rounded-full"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {subAdmins.length > 0 && (
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  setPage={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={closeModal}>
        <div className="p-[30px]">
          <div className="pb-[21px] border-b border-[#EEEEEE] mb-[19px]">
            <h2 className="text-[22px] font-medium">
              {editId
                ? t("managesubAdmin").modal.editTitle
                : t("managesubAdmin").modal.addTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
            <Input
              label={t("managesubAdmin").modal.labels.name}
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              error={errors.name}
            />
            <Input
              label={t("managesubAdmin").modal.labels.email}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              error={errors.email}
              readOnly={!!editId}
            />
            <Input
              label={t("managesubAdmin").modal.labels.password}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleFormChange}
              error={errors.password}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword((prev) => !prev)}
            />
            <Input
              label={t("managesubAdmin").modal.labels.phone}
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleFormChange}
              error={errors.phoneNumber}
            />

            <div>
              <label className="text-sm mb-[8px] block">
                {t("managesubAdmin").modal.labels.selectRole} *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleFormChange}
                className="border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50]"
              >
                <option value="" disabled>
                  {" "}
                  {t("managesubAdmin").modal.labels.selectRole}
                </option>
                <option value="hotel_manager">
                  {t("managesubAdmin").roles.hotel_manager}
                </option>
                <option value="restaurant_manager">
                  {t("managesubAdmin").roles.restaurant_manager}
                </option>
                <option value="support_staff">
                  {t("managesubAdmin").roles.support_staff}
                </option>
                <option value="operations_manager">
                  {t("managesubAdmin").roles.operations_manager}
                </option>
                <option value="marketing_manager">
                  {t("managesubAdmin").roles.marketing_manager}
                </option>
                <option value="finance_officer">
                  {t("managesubAdmin").roles.finance_officer}
                </option>
                <option value="customer_service">
                  {t("managesubAdmin").roles.customer_service}
                </option>
                <option value="hr_manager">
                  {t("managesubAdmin").roles.hr_manager}
                </option>
                <option value="technical_support">
                  {t("managesubAdmin").roles.technical_support}
                </option>
                <option value="logistics_manager">
                  {t("managesubAdmin").roles.logistics_manager}
                </option>
                <option value="cleaning_supervisor">
                  {t("managesubAdmin").roles.cleaning_supervisor}
                </option>
                <option value="security_head">
                  {t("managesubAdmin").roles.security_head}
                </option>
                <option value="chef_head">
                  {t("managesubAdmin").roles.chef_head}
                </option>
              </select>
              {errors.role && (
                <p className="text-red-500 text-sm mt-1">{errors.role}</p>
              )}
            </div>

            {editId && (
              <div>
                <label className="text-sm mb-[8px] block">
                  {t("managesubAdmin").modal.labels.status} *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  className="border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none focus:border-[#4CAF50]"
                >
                  <option value="active">
                    {t("managesubAdmin").status.active}
                  </option>
                  <option value="inactive">
                    {t("managesubAdmin").status.inactive}
                  </option>
                  <option value="suspended">
                    {t("managesubAdmin").status.suspended}
                  </option>
                </select>
                {errors.status && (
                  <p className="text-red-500 text-sm mt-1">{errors.status}</p>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-[30px] justify-end pt-[23px]">
            <button
              onClick={closeModal}
              className="text-white py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] font-bold"
            >
              {t("managesubAdmin").modal.buttons.cancel}
            </button>
            <button
              onClick={handleAddOrUpdate}
              disabled={isAdding || isUpdating}
              className="bg-[#4CAF50] text-white py-[14px] px-[30px] rounded-[2px] text-[20px] font-bold hover:bg-[#209b25]"
            >
              {(isAdding || isUpdating) ? t("header").loading : editId
                ? t("managesubAdmin").modal.buttons.update
                : t("managesubAdmin").modal.buttons.add}
            </button>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

const Input = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  readOnly = false,
  showPassword = false,
  onTogglePassword,
}) => {
  const { t } = useTranslation();
  
  // Get the proper placeholder from translation files
  const getPlaceholder = () => {
    const placeholders = t("managesubAdmin").modal.placeholders;
    
    // Map field names to placeholder keys
    if (placeholders) {
      if (name === "name" && placeholders.name) return placeholders.name;
      if (name === "email" && placeholders.email) return placeholders.email;
      if (name === "password" && placeholders.password) return placeholders.password;
      if (name === "phoneNumber" && placeholders.phone) return placeholders.phone;
    }
    
    // Fallback to old method if placeholders not available
    return `Enter ${label.toLowerCase()}`;
  };
  
  const placeholder = getPlaceholder();
  
  const isPasswordField = type === "password";
  const inputType = isPasswordField ? (showPassword ? "text" : "password") : type;

  return (
    <div className="w-full">
      <label className="text-sm mb-[8px] block">{label} *</label>
      <div className="relative">
        <input
          type={inputType}
          name={name}
          value={value}
          readOnly={readOnly}
          onChange={onChange}
          placeholder={placeholder}
          className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${isPasswordField ? "pr-12" : ""} ${
            error ? "border-red-500" : "focus:border-[#4CAF50]"
          }`}
        />
        {isPasswordField && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
          >
            {showPassword ? <AiFillEyeInvisible size={20} /> : <Eye size={20} />}
          </button>
        )}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default AdminSubAdminRoles;
